import type { HubMe } from './useHubVerification';
import type { HubPayload } from './useMachineHub';

/** What the assistant is told about the sticker's machine: the catalog model (machine_id) for anyone, the unit id only once the visitor verified. */
export interface AgentMachineContext { make: string | null; model: string; category: string | null; machine_id: number | null; customer_machine_id: number | null }

export function hubMachineContext(hub: HubPayload, me: HubMe | null): AgentMachineContext {
  return { make: hub.machine.make, model: hub.machine.model, category: hub.machine.category, machine_id: hub.machine.id, customer_machine_id: me?.unit?.customer_machine_id ?? null };
}

/** Edge-function chat body with the sticker's machine on it. Sent on the agent call AND every
 *  service call: the service pipeline seeds its equipment from it once, at its first turn. */
export function withMachine<T extends Record<string, unknown>>(body: T, machine: AgentMachineContext | null): T & { machine?: AgentMachineContext } {
  return machine ? { ...body, machine } : body;
}

/** Resume key: the org's public chat and each sticker's chat (per unit or per model) never share a
 *  transcript, and a verified session never resumes one that started unverified (nor the reverse). */
export function agentStorageKey(organizationId: string, machine: AgentMachineContext | null, verified: boolean): string {
  const base = `opervia-agent-chat:${organizationId}`;
  const scope = machine ? (machine.customer_machine_id != null ? `:hub:unit:${machine.customer_machine_id}` : `:hub:model:${machine.machine_id ?? machine.model}`) : '';
  return `${base}${scope}${verified ? ':v' : ''}`;
}

/** Proof of a verified hub session for the service hand-off: the sticker, the code id and the secret /verify/check minted for it.
 *  The AI service re-validates it against the consumed code's binding to the sticker; it is never trusted as-is. */
export interface HubIdentity { token: string; code_id: number; session: string }

/** Null unless the session is verified with all three parts in hand, so no identity leaves the browser for an unverified visitor. */
export function hubIdentity(token: string, v: { verified: boolean; codeId: number | null; session: string | null }): HubIdentity | null {
  return v.verified && v.codeId != null && v.session ? { token, code_id: v.codeId, session: v.session } : null;
}

/** Service chat body with the hub identity on it; the body untouched when there is none (unverified, expired). */
export function withHub<T extends Record<string, unknown>>(body: T, hub: HubIdentity | null): T & { hub?: HubIdentity } {
  return hub ? { ...body, hub } : body;
}
